/**
 * Компонент вкладок для навигации по разделам контента
 * 
 * Отвечает за:
 * - Отображение вкладок для различных категорий контента
 * - Обработку переключения между вкладками
 * - Отображение счетчика элементов для тренировок
 * - Навигацию к соответствующим маршрутам контента
 */
import React, { memo, useCallback, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './ContentTabs.module.scss';
import MenuIcon from '../icons/MenuIcon';
import CloseIcon from '../icons/CloseIcon';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Интерфейс для компонента вкладок контента
 */
interface ContentTabsProps {
    currentTab: number;
    onTabChange: (newValue: number) => void;
    dailyTrainingsCount: number;
}

/**
 * Компонент вкладок для навигации между разделами контента
 * 
 * Оптимизирован с использованием React.memo для предотвращения ненужных перерендеров
 * 
 * @param props - свойства компонента 
 * @returns Оптимизированный компонент вкладок
 */
const ContentTabs: React.FC<ContentTabsProps> = memo(({ currentTab, onTabChange }) => {
    const { t } = useTranslation();
    const { admin } = useAuth();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const mobileMenuRef = useRef<HTMLDivElement>(null);
    const menuButtonRef = useRef<HTMLButtonElement>(null);

    /**
     * Обработчик изменения вкладки с мемоизацией
     * для предотвращения пересоздания функции при каждом рендере
     */
    const handleTabChange = useCallback((index: number) => {
        onTabChange(index);
        setMobileMenuOpen(false);
    }, [onTabChange]);

    /**
     * Обработчик открытия/закрытия мобильного меню
     */
    const toggleMobileMenu = useCallback(() => {
        setMobileMenuOpen(prev => !prev);
    }, []);

    /**
     * Обработчик клика вне мобильного меню
     */
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent | TouchEvent) => {
            if (
                mobileMenuOpen &&
                mobileMenuRef.current &&
                !mobileMenuRef.current.contains(event.target as Node) &&
                menuButtonRef.current &&
                !menuButtonRef.current.contains(event.target as Node)
            ) {
                setMobileMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside as EventListener);
        document.addEventListener('touchstart', handleClickOutside as EventListener);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside as EventListener);
            document.removeEventListener('touchstart', handleClickOutside as EventListener);
        };
    }, [mobileMenuOpen]);

    /**
     * Содержимое вкладок, выделенное для переиспользования
     */
    const tabsContent = (
        <>
            <button
                className={styles.tab}
                data-active={currentTab === 0}
                onClick={() => handleTabChange(0)}
                data-testid="trainings-tab"
            >
                {t('content.tabs.trainings')}
            </button>

            <button
                className={styles.tab}
                data-active={currentTab === 1}
                onClick={() => handleTabChange(1)}
                data-testid="bonus-tab"
            >
                {t('content.tabs.bonus')}
            </button>

            <button
                className={styles.tab}
                data-active={currentTab === 2}
                onClick={() => handleTabChange(2)}
                data-testid="instruction-tab"
            >
                {t('content.tabs.instruction')}
            </button>

            <button
                className={styles.tab}
                data-active={currentTab === 3}
                onClick={() => handleTabChange(3)}
                data-testid="presentation-tab"
            >
                {t('content.tabs.presentation')}
            </button>

            <button
                className={styles.tab}
                data-active={currentTab === 4}
                onClick={() => handleTabChange(4)}
                data-testid="before-after-tab"
            >
                {t('content.tabs.beforeAfter')}
            </button>

            <button
                className={styles.tab}
                data-active={currentTab === 5}
                onClick={() => handleTabChange(5)}
                data-testid="free-video-tab"
            >
                {t('content.tabs.freeVideo')}
            </button>

            {admin?.language === 'RU' && (
                <button
                    className={styles.tab}
                    data-active={currentTab === 6}
                    onClick={() => handleTabChange(6)}
                    data-testid="recordings-tab"
                >
                    {t('content.tabs.recordings')}
                </button>
            )}
        </>
    );

    return (
        <>
            {/* Кнопка меню-гамбургер для мобильных и планшетов */}
            <button
                className={styles.menuButton}
                onClick={toggleMobileMenu}
                aria-label={t('common.toggleMenu')}
                ref={menuButtonRef}
            >
                <MenuIcon />
            </button>

            {/* Вкладки для десктопа */}
            <div className={styles.tabsContainer}>
                {tabsContent}
            </div>

            {/* Мобильное меню */}
            {mobileMenuOpen && (
                <div className={`${styles.mobileMenu} ${mobileMenuOpen ? styles.open : ''}`}>
                    <div
                        className={styles.mobileMenuContent}
                        ref={mobileMenuRef}
                    >
                        <button
                            className={styles.closeButton}
                            onClick={toggleMobileMenu}
                            aria-label={t('common.closeMenu')}
                        >
                            <CloseIcon />
                        </button>
                        {tabsContent}
                    </div>
                </div>
            )}
        </>
    );
});

// Добавляем отображаемое имя для инструментов разработчика
ContentTabs.displayName = 'ContentTabs';

export default ContentTabs;